import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Typography,
  Box,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import api from "../../utils/api";
import { useCrypto } from "../../contexts/CryptoContext";
import {
  successAlert,
  errorAlert,
  loadingAlert,
  closeAlert,
} from "../../utils/swalConfig";

const WithdrawalModal = ({ open, onClose, onSuccess, availableBalance }) => {
  const { networks } = useCrypto();
  const [withdrawalMethod, setWithdrawalMethod] = useState("crypto");
  const [cryptoFormData, setCryptoFormData] = useState({
    network: "",
    amount: "",
    walletAddress: "",
  });
  const [bankFormData, setBankFormData] = useState({
    bank: "",
    accountNumber: "",
    accountName: "",
    amount: "",
  });
  const [bankList, setBankList] = useState([]);
  const [loadingBanks, setLoadingBanks] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && withdrawalMethod === "bank") {
      fetchBanks();
    }
  }, [withdrawalMethod, open]);

  const fetchBanks = async () => {
    try {
      setLoadingBanks(true);
      const token = localStorage.getItem("jwt");
      const response = await api.get("/banks", {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.status === "success") {
        // Filter for Nigerian banks only
        const nigerianBanks = response.data.data.banks.filter(
          (bank) => bank.isNigerianBank
        );
        setBankList(nigerianBanks);
      }
    } catch (error) {
      errorAlert("Error", "Failed to fetch banks");
    } finally {
      setLoadingBanks(false);
    }
  };

  const selectedNetwork = networks.find(
    (n) => n.name === cryptoFormData.network
  );
  const cryptoAmount = selectedNetwork
    ? Number(cryptoFormData.amount) / selectedNetwork.exchangeRate
    : 0;

  const handleMethodChange = (event, newValue) => {
    setWithdrawalMethod(newValue);
  };

  const handleCryptoSubmit = async (e) => {
    e.preventDefault();
    onClose();
    loadingAlert("Processing crypto withdrawal...");

    try {
      const token = localStorage.getItem("jwt");
      await api.post("/users/withdraw", cryptoFormData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      closeAlert();
      await successAlert(
        "Success!",
        "Crypto withdrawal request submitted successfully"
      );
      onSuccess();
    } catch (err) {
      closeAlert();
      errorAlert(
        "Error",
        err.response?.data?.message || "Failed to submit withdrawal"
      );
    }
  };

  const handleBankSubmit = async (e) => {
    e.preventDefault();
    onClose();
    setSubmitting(true);
    loadingAlert("Processing bank withdrawal...");

    try {
      const token = localStorage.getItem("jwt");
      await api.post("/users/withdraw-bank", bankFormData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      closeAlert();
      await successAlert(
        "Success!",
        "Bank withdrawal request submitted successfully"
      );
      onSuccess();
    } catch (err) {
      closeAlert();
      errorAlert(
        "Error",
        err.response?.data?.message || "Failed to submit bank withdrawal"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Withdraw Funds</DialogTitle>

      <DialogContent>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Available Balance: ₦{availableBalance?.toLocaleString()}
        </Typography>

        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
          <Tabs
            value={withdrawalMethod}
            onChange={handleMethodChange}
            variant="fullWidth"
          >
            <Tab label="Crypto" value="crypto" />
            <Tab label="Bank Transfer" value="bank" />
          </Tabs>
        </Box>

        {withdrawalMethod === "crypto" ? (
          <form id="crypto-form" onSubmit={handleCryptoSubmit}>
            <TextField
              fullWidth
              select
              label="Select Network"
              value={cryptoFormData.network}
              onChange={(e) =>
                setCryptoFormData({
                  ...cryptoFormData,
                  network: e.target.value,
                })
              }
              required
              sx={{ mb: 2 }}
            >
              {networks.map((network) => (
                <MenuItem key={network._id} value={network.name}>
                  {network.name} ({network.symbol})
                </MenuItem>
              ))}
            </TextField>

            <TextField
              fullWidth
              label="Amount (NGN)"
              type="number"
              value={cryptoFormData.amount}
              onChange={(e) =>
                setCryptoFormData({ ...cryptoFormData, amount: e.target.value })
              }
              required
              inputProps={{
                max: availableBalance,
                min:
                  selectedNetwork?.minimumWithdrawal *
                    selectedNetwork?.exchangeRate || 0,
              }}
              helperText={
                selectedNetwork
                  ? `Minimum: $${selectedNetwork.minimumWithdrawal} (₦${(
                      selectedNetwork.minimumWithdrawal *
                      selectedNetwork.exchangeRate
                    ).toLocaleString()})`
                  : "Select a network first"
              }
              sx={{ mb: 2 }}
            />

            {selectedNetwork && cryptoFormData.amount && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  You will receive: ${cryptoAmount.toFixed(2)}{" "}
                  {selectedNetwork.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Rate: ₦{selectedNetwork.exchangeRate.toLocaleString()}/$
                </Typography>
              </Box>
            )}

            <TextField
              fullWidth
              label={`${selectedNetwork?.name || "Wallet"} Address`}
              value={cryptoFormData.walletAddress}
              onChange={(e) =>
                setCryptoFormData({
                  ...cryptoFormData,
                  walletAddress: e.target.value,
                })
              }
              required
              placeholder="Enter your wallet address"
              helperText="Double check your wallet address before submitting"
            />
          </form>
        ) : (
          <form id="bank-form" onSubmit={handleBankSubmit}>
            {loadingBanks ? (
              <Box sx={{ display: "flex", justifyContent: "center", my: 3 }}>
                <CircularProgress />
              </Box>
            ) : bankList.length === 0 ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                No banks available. Please try again later.
              </Alert>
            ) : (
              <>
                <TextField
                  fullWidth
                  select
                  label="Select Bank"
                  value={bankFormData.bank}
                  onChange={(e) =>
                    setBankFormData({ ...bankFormData, bank: e.target.value })
                  }
                  required
                  sx={{ mb: 2 }}
                >
                  {bankList.map((bank) => (
                    <MenuItem key={bank._id} value={bank.bankName}>
                      {bank.bankName}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  fullWidth
                  label="Account Number"
                  type="text"
                  value={bankFormData.accountNumber}
                  onChange={(e) =>
                    setBankFormData({
                      ...bankFormData,
                      accountNumber: e.target.value,
                    })
                  }
                  required
                  inputProps={{ maxLength: 10, pattern: "[0-9]{10}" }}
                  helperText="Enter a valid 10-digit account number"
                  sx={{ mb: 2 }}
                />

                <TextField
                  fullWidth
                  label="Account Name"
                  type="text"
                  value={bankFormData.accountName}
                  onChange={(e) =>
                    setBankFormData({
                      ...bankFormData,
                      accountName: e.target.value,
                    })
                  }
                  required
                  helperText="Enter account name as it appears on your bank account"
                  sx={{ mb: 2 }}
                />

                <TextField
                  fullWidth
                  label="Amount (NGN)"
                  type="number"
                  value={bankFormData.amount}
                  onChange={(e) =>
                    setBankFormData({ ...bankFormData, amount: e.target.value })
                  }
                  required
                  inputProps={{
                    max: availableBalance,
                    min: 2000, // Minimum withdrawal for banks is ₦2,000
                  }}
                  helperText="Minimum withdrawal: ₦2,000"
                />

                <Alert severity="info" sx={{ mt: 2 }}>
                  Bank withdrawals typically process within 24 hours on business
                  days.
                </Alert>
              </>
            )}
          </form>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {withdrawalMethod === "crypto" ? (
          <LoadingButton
            variant="contained"
            type="submit"
            form="crypto-form"
            disabled={
              !cryptoFormData.network ||
              !cryptoFormData.amount ||
              !cryptoFormData.walletAddress ||
              Number(cryptoFormData.amount) > availableBalance ||
              (selectedNetwork &&
                Number(cryptoFormData.amount) <
                  selectedNetwork.minimumWithdrawal *
                    selectedNetwork.exchangeRate)
            }
          >
            Submit Crypto Withdrawal
          </LoadingButton>
        ) : (
          <LoadingButton
            variant="contained"
            type="submit"
            form="bank-form"
            loading={submitting}
            disabled={
              !bankFormData.bank ||
              !bankFormData.accountNumber ||
              !bankFormData.accountName ||
              !bankFormData.amount ||
              bankFormData.accountNumber.length !== 10 ||
              Number(bankFormData.amount) > availableBalance ||
              Number(bankFormData.amount) < 2000
            }
          >
            Submit Bank Withdrawal
          </LoadingButton>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default WithdrawalModal;
